/**
 * Internal Links Management Page
 * 
 * Redesigned page focused on internal linking analysis with 5 tabs:
 * - Summary: Overview metrics, link equity insights
 * - Source Pages: Pages linking OUT (outbound analysis)
 * - Target Pages: Pages receiving links (inbound analysis)
 * - Orphan Pages: Zero-link pages needing attention
 * - Broken Links: Internal links pointing to 4xx/5xx targets
 * - Link Changes: Historical GAINED/LOST tracking
 * 
 * V229: Internal links focus with modular tabs for independent improvement
 */

import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Tabs,
  Tab,
  Button,
  Chip,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Link as MuiLink,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import {
  OpenInNew as OpenIcon,
  Link as LinkIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  CallMade as OutboundIcon,
  CallReceived as InboundIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  Timeline as TimelineIcon,
  Hub as HubIcon,
  ArrowBack as ArrowBackIcon,
  LinkOff as OrphanIcon,
  Assessment as AssessmentIcon,
  CalendarMonth as CalendarIcon,
} from '@mui/icons-material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import {
  getInternalLinks,
  getInternalLinksSummary,
  getInternalOrphanPages,
  getBrokenInternalLinks,
  getInternalSourcePages,
  getInternalTargetPages,
  getLinkChanges,
  getDailyChangeCounts,
  getInternalLinksSnapshots,
} from '@/services/customer';
import type {
  InternalLinksResponse,
  InternalLinksSummaryDTO,
  InternalOrphanPagesResponse,
  BrokenInternalLinksResponse,
  InternalSourcePagesResponse,
  InternalTargetPagesResponse,
  LinkChangesResponse,
  DailyChangeCountsDTO,
} from '@/types/customer';
import { useSiteStore } from '@/store/site.store';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`internal-links-tabpanel-${index}`}
      aria-labelledby={`internal-links-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

// Color palette for charts
const COLORS = ['#2196f3', '#4caf50', '#ff9800', '#f44336', '#9c27b0', '#00bcd4'];

export default function CustomerInternalLinksPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const tabParam = searchParams.get('tab');
    return tabParam ? parseInt(tabParam, 10) : 0;
  });
  const { selectedSite } = useSiteStore();
  const siteId = selectedSite?.id;
  
  // Summary state
  const [summary, setSummary] = useState<InternalLinksSummaryDTO | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  
  // Source Pages state
  const [sourcePages, setSourcePages] = useState<InternalSourcePagesResponse | null>(null);
  const [sourceLoading, setSourceLoading] = useState(false);
  const [sourcePage, setSourcePage] = useState(0);
  const [sourcePageSize, setSourcePageSize] = useState(20);
  
  // Target Pages state
  const [targetPages, setTargetPages] = useState<InternalTargetPagesResponse | null>(null);
  const [targetLoading, setTargetLoading] = useState(false);
  const [targetPage, setTargetPage] = useState(0);
  const [targetPageSize, setTargetPageSize] = useState(20);
  
  // Orphan Pages state
  const [orphanPages, setOrphanPages] = useState<InternalOrphanPagesResponse | null>(null);
  const [orphanLoading, setOrphanLoading] = useState(false);

  // Broken Links state
  const [brokenLinks, setBrokenLinks] = useState<BrokenInternalLinksResponse | null>(null);
  const [brokenLoading, setBrokenLoading] = useState(false);
  const [brokenPage, setBrokenPage] = useState(0);
  const [brokenPageSize, setBrokenPageSize] = useState(20);
  
  // Link Changes state
  const [linkChanges, setLinkChanges] = useState<LinkChangesResponse | null>(null);
  const [dailyChanges, setDailyChanges] = useState<DailyChangeCountsDTO | null>(null);
  const [changesLoading, setChangesLoading] = useState(false);
  const [changesPage, setChangesPage] = useState(0);
  const [changesPageSize, setChangesPageSize] = useState(20);
  const [changeTypeFilter, setChangeTypeFilter] = useState<'GAINED' | 'LOST' | ''>('');
  const [changeDateRange, setChangeDateRange] = useState(30);
  
  // Breakdown dialog state
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [breakdownSourcePage, setBreakdownSourcePage] = useState<string | null>(null);
  const [breakdownTargetPage, setBreakdownTargetPage] = useState<string | null>(null);
  const [breakdownLinks, setBreakdownLinks] = useState<InternalLinksResponse | null>(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [breakdownPage, setBreakdownPage] = useState(0);
  const [breakdownPageSize, setBreakdownPageSize] = useState(20);
  
  // Snapshot history state
  const [availableSnapshots, setAvailableSnapshots] = useState<string[]>([]);
  const [selectedSnapshot, setSelectedSnapshot] = useState<string>('');
  const [snapshotsLoading, setSnapshotsLoading] = useState(false);
  
  const [error, setError] = useState<string | null>(null);

  // Load available snapshots on mount
  useEffect(() => {
    if (siteId) {
      loadAvailableSnapshots();
    }
  }, [siteId]);

  // Load data based on active tab
  useEffect(() => {
    loadTabData();
  }, [activeTab, sourcePage, targetPage, brokenPage, brokenPageSize, changesPage, siteId]);

  useEffect(() => {
    if (!breakdownOpen) return;
    if (!breakdownSourcePage && !breakdownTargetPage) return;
    loadBreakdownLinks();
  }, [breakdownOpen, breakdownSourcePage, breakdownTargetPage, breakdownPage, breakdownPageSize]);

  const loadTabData = async () => {
    setError(null);
    if (!siteId) {
      setError('Please select a site to view internal links data.');
      return;
    }
    try {
      switch (activeTab) {
        case 0: // Summary
          await loadSummary();
          break;
        case 1: // Source Pages
          await loadSourcePages();
          break;
        case 2: // Target Pages
          await loadTargetPages();
          break;
        case 3: // Orphan Pages
          await loadOrphanPages();
          break;
        case 4: // Link Changes
          await loadLinkChanges();
          break;
        case 5: // Broken Links
          await loadBrokenLinks();
          break;
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    }
  };

  const loadSummary = async () => {
    setSummaryLoading(true);
    try {
      // Only load summary metrics - don't preload other tabs
      const summaryData = await getInternalLinksSummary();
      setSummary(summaryData);
    } finally {
      setSummaryLoading(false);
    }
  };

  const loadSourcePages = async () => {
    setSourceLoading(true);
    try {
      const data = await getInternalSourcePages(sourcePage, sourcePageSize);
      setSourcePages(data);
    } finally {
      setSourceLoading(false);
    }
  };

  const loadTargetPages = async () => {
    setTargetLoading(true);
    try {
      const data = await getInternalTargetPages(targetPage, targetPageSize);
      setTargetPages(data);
    } finally {
      setTargetLoading(false);
    }
  };

  const loadOrphanPages = async () => {
    setOrphanLoading(true);
    try {
      const data = await getInternalOrphanPages();
      setOrphanPages(data);
    } finally {
      setOrphanLoading(false);
    }
  };

  const loadBrokenLinks = async () => {
    setBrokenLoading(true);
    try {
      const data = await getBrokenInternalLinks(brokenPage, brokenPageSize);
      setBrokenLinks(data);
    } finally {
      setBrokenLoading(false);
    }
  };

  const loadLinkChanges = async () => {
    setChangesLoading(true);
    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - changeDateRange);
      
      const [changesData, dailyData] = await Promise.all([
        getLinkChanges(
          startDate.toISOString().split('T')[0],
          endDate.toISOString().split('T')[0],
          changesPage,
          changesPageSize,
          changeTypeFilter || undefined,
          'INTERNAL' // Filter to internal links only
        ),
        getDailyChangeCounts(
          startDate.toISOString().split('T')[0],
          endDate.toISOString().split('T')[0],
          'INTERNAL' // Filter to internal links only
        ),
      ]);
      setLinkChanges(changesData);
      setDailyChanges(dailyData);
    } finally {
      setChangesLoading(false);
    }
  };

  const loadBreakdownLinks = async () => {
    setBreakdownLoading(true);
    try {
      const data = await getInternalLinks(
        breakdownPage,
        breakdownPageSize,
        breakdownSourcePage || undefined,
        breakdownTargetPage || undefined
      );
      setBreakdownLinks(data);
    } finally {
      setBreakdownLoading(false);
    }
  };

  const loadAvailableSnapshots = async () => {
    setSnapshotsLoading(true);
    try {
      const snapshots = await getInternalLinksSnapshots();
      setAvailableSnapshots(snapshots);
      // Select the most recent snapshot by default if not already selected
      if (snapshots.length > 0 && !selectedSnapshot) {
        setSelectedSnapshot(snapshots[0]);
      }
    } catch (err: any) {
      console.error('Failed to load snapshots:', err);
    } finally {
      setSnapshotsLoading(false);
    }
  };

  const handleSnapshotChange = (event: SelectChangeEvent<string>) => {
    setSelectedSnapshot(event.target.value);
    // Reload current tab data with new snapshot
    loadTabData();
  };

  const formatSnapshotLabel = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) {
      return `Today (${date.toLocaleDateString()})`;
    } else if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday (${date.toLocaleDateString()})`;
    }
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setSearchParams({ tab: newValue.toString() });
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat().format(num);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString();
  };

  const extractPath = (url: string) => {
    try {
      const urlObj = new URL(url);
      return urlObj.pathname || '/';
    } catch {
      return url;
    }
  };

  // Calculate link equity distribution for chart
  const getLinkEquityData = () => {
    if (!targetPages?.pages) return [];
    const totalLinks = targetPages.pages.reduce((sum, p) => sum + p.totalInboundLinks, 0);
    return targetPages.pages.slice(0, 5).map((page, index) => ({
      name: extractPath(page.targetPage),
      value: page.totalInboundLinks,
      percentage: totalLinks > 0 ? ((page.totalInboundLinks / totalLinks) * 100).toFixed(1) : 0,
    }));
  };

  // Transform daily changes data for chart
  const chartData = dailyChanges?.dailyCounts
    .filter(item => item.linkType === 'INTERNAL')
    .reduce((acc, item) => {
      const existing = acc.find(d => d.date === item.date);
      if (existing) {
        existing[item.changeType.toLowerCase()] = item.count;
      } else {
        acc.push({
          date: formatDate(item.date),
          [item.changeType.toLowerCase()]: item.count,
        });
      }
      return acc;
    }, [] as any[]) || [];

  return (
    <Box sx={{
      maxWidth: { xs: 400, sm: 768, md: 900, lg: 1200 },
      mx: 'auto',
      px: { xs: 2, sm: 3, md: 4 },
      py: 3
    }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/early-stage')}
          sx={{ mb: 2 }}
        >
          Back to Dashboard
        </Button>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LinkIcon />
              Internal Links Analysis
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Analyze your internal linking structure to optimize link equity distribution and discover orphan pages
            </Typography>
          </Box>
          
          {/* Snapshot Selector */}
          {availableSnapshots.length > 0 && (
            <Box sx={{ minWidth: 280 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="snapshot-select-label">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CalendarIcon fontSize="small" />
                    Data Snapshot
                  </Box>
                </InputLabel>
                <Select
                  labelId="snapshot-select-label"
                  value={selectedSnapshot}
                  label="Data Snapshot"
                  onChange={handleSnapshotChange}
                  disabled={snapshotsLoading}
                >
                  {availableSnapshots.map((snapshot) => (
                    <MenuItem key={snapshot} value={snapshot}>
                      {formatSnapshotLabel(snapshot)}
                      {snapshot === availableSnapshots[0] && (
                        <Chip 
                          label="Latest" 
                          size="small" 
                          color="primary" 
                          sx={{ ml: 1, height: 20 }} 
                        />
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {availableSnapshots.length} snapshot{availableSnapshots.length !== 1 ? 's' : ''} available
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          <Tab icon={<AssessmentIcon />} iconPosition="start" label="Summary" />
          <Tab icon={<OutboundIcon />} iconPosition="start" label="Source Pages" />
          <Tab icon={<InboundIcon />} iconPosition="start" label="Target Pages" />
          <Tab 
            icon={<OrphanIcon />} 
            iconPosition="start" 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Orphan Pages
                {summary && summary.orphanPagesCount > 0 && (
                  <Chip label={summary.orphanPagesCount} size="small" color="warning" />
                )}
              </Box>
            } 
          />
          <Tab icon={<TimelineIcon />} iconPosition="start" label="Link Changes" />
          <Tab
            icon={<WarningIcon />}
            iconPosition="start"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Broken Links
                {brokenLinks && brokenLinks.totalCount > 0 && (
                  <Chip label={brokenLinks.totalCount} size="small" color="error" />
                )}
              </Box>
            }
          />
        </Tabs>
      </Paper>

      {/* Tab 0: Summary */}
      <TabPanel value={activeTab} index={0}>
        {summaryLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Summary Metrics */}
            {summary && (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <LinkIcon color="primary" />
                        <Typography variant="body2" color="text.secondary">Total Internal Links</Typography>
                      </Box>
                      <Typography variant="h4">{formatNumber(summary.totalInternalLinks)}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Snapshot: {formatDate(summary.snapshotDate)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <OutboundIcon color="info" />
                        <Typography variant="body2" color="text.secondary">Source Pages</Typography>
                      </Box>
                      <Typography variant="h4">{formatNumber(summary.uniqueSourcePages)}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Pages linking out
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <InboundIcon color="success" />
                        <Typography variant="body2" color="text.secondary">Target Pages</Typography>
                      </Box>
                      <Typography variant="h4">{formatNumber(summary.uniqueTargetPages)}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Pages receiving links
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: summary.orphanPagesCount > 0 ? 'warning.light' : 'success.light' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <WarningIcon color={summary.orphanPagesCount > 0 ? 'warning' : 'success'} />
                        <Typography variant="body2" color="text.secondary">Orphan Pages</Typography>
                      </Box>
                      <Typography variant="h4" color={summary.orphanPagesCount > 0 ? 'warning.dark' : 'success.dark'}>
                        {formatNumber(summary.orphanPagesCount)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {summary.orphanPagesCount > 0 ? 'Need internal links!' : 'All pages connected'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}

            {/* Link Coverage Insight */}
            {summary && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <HubIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Link Coverage Analysis
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Source Coverage
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <LinearProgress 
                            variant="determinate" 
                            value={summary.uniqueTargetPages > 0 ? (summary.uniqueSourcePages / summary.uniqueTargetPages) * 100 : 0}
                            sx={{ flexGrow: 1, height: 10, borderRadius: 5 }}
                          />
                          <Typography variant="body2">
                            {summary.uniqueSourcePages} of {summary.uniqueTargetPages}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {summary.uniqueSourcePages} pages link to other content
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Average Links Per Source
                        </Typography>
                        <Typography variant="h5">
                          {summary.uniqueSourcePages > 0 
                            ? (summary.totalInternalLinks / summary.uniqueSourcePages).toFixed(1)
                            : 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          outbound links per page
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Top Link Recipients
                      </Typography>
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={getLinkEquityData()} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" />
                          <YAxis dataKey="name" type="category" width={150} tick={{ fontSize: 12 }} />
                          <RechartsTooltip formatter={(value: number) => [formatNumber(value), 'Inbound Links']} />
                          <Bar dataKey="value" fill="#2196f3" />
                        </BarChart>
                      </ResponsiveContainer>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}

            {/* Quick Actions */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Top Hub Pages</Typography>
                    <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                      Pages with most outbound links
                    </Typography>
                    {sourcePages?.pages.slice(0, 5).map((page, index) => (
                      <Box key={page.sourcePage} sx={{ py: 1, borderBottom: index < 4 ? '1px solid' : 'none', borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Tooltip title={page.sourcePage}>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                              {extractPath(page.sourcePage)}
                            </Typography>
                          </Tooltip>
                          <Chip label={`${page.totalOutboundLinks} links`} size="small" color="info" />
                        </Box>
                      </Box>
                    ))}
                    <Button 
                      fullWidth 
                      sx={{ mt: 2 }} 
                      onClick={() => { setActiveTab(1); setSearchParams({ tab: '1' }); }}
                    >
                      View All Source Pages
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Most Linked Pages</Typography>
                    <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                      Pages receiving most internal links
                    </Typography>
                    {targetPages?.pages.slice(0, 5).map((page, index) => (
                      <Box key={page.targetPage} sx={{ py: 1, borderBottom: index < 4 ? '1px solid' : 'none', borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Tooltip title={page.targetPage}>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                              {extractPath(page.targetPage)}
                            </Typography>
                          </Tooltip>
                          <Chip label={`${page.totalInboundLinks} links`} size="small" color="success" />
                        </Box>
                      </Box>
                    ))}
                    <Button 
                      fullWidth 
                      sx={{ mt: 2 }} 
                      onClick={() => { setActiveTab(2); setSearchParams({ tab: '2' }); }}
                    >
                      View All Target Pages
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card sx={{ bgcolor: orphanPages && orphanPages.orphanPagesCount > 0 ? 'warning.light' : 'inherit' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      <WarningIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'warning.main' }} />
                      Orphan Pages Alert
                    </Typography>
                    <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                      Pages with no inbound links (SEO issue)
                    </Typography>
                    {orphanPages?.orphanPages.slice(0, 5).map((pageUrl, index) => (
                      <Box key={pageUrl} sx={{ py: 1, borderBottom: index < 4 ? '1px solid' : 'none', borderColor: 'divider' }}>
                        <Tooltip title={pageUrl}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
                            {extractPath(pageUrl)}
                          </Typography>
                        </Tooltip>
                      </Box>
                    ))}
                    {orphanPages && orphanPages.orphanPagesCount > 5 && (
                      <Typography variant="caption" color="text.secondary">
                        + {orphanPages.orphanPagesCount - 5} more orphan pages
                      </Typography>
                    )}
                    <Button 
                      fullWidth 
                      color="warning"
                      variant="contained"
                      sx={{ mt: 2 }} 
                      onClick={() => { setActiveTab(3); setSearchParams({ tab: '3' }); }}
                    >
                      Review All Orphan Pages
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </>
        )}
      </TabPanel>

      {/* Tab 1: Source Pages (Outbound) */}
      <TabPanel value={activeTab} index={1}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6">
                  <OutboundIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Source Pages (Outbound Links)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pages that link to other content on your site. High outbound count indicates hub/navigation pages.
                </Typography>
              </Box>
              <IconButton onClick={loadSourcePages} disabled={sourceLoading}>
                <RefreshIcon />
              </IconButton>
            </Box>

            {sourceLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ minWidth: 400 }}>Source Page URL</TableCell>
                        <TableCell align="right">Total Outbound Links</TableCell>
                        <TableCell align="right">Unique Targets</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sourcePages?.pages.map((page, index) => (
                        <TableRow key={page.sourcePage} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {index < 3 && <Chip label={`#${index + 1}`} size="small" color="primary" />}
                              <MuiLink href={page.sourcePage} target="_blank" rel="noopener" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Tooltip title={page.sourcePage}>
                                  <Typography variant="body2" noWrap sx={{ maxWidth: 350 }}>
                                    {page.sourcePage}
                                  </Typography>
                                </Tooltip>
                                <OpenIcon fontSize="small" />
                              </MuiLink>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Chip 
                              label={formatNumber(page.totalOutboundLinks)} 
                              color={page.totalOutboundLinks >= 10 ? 'primary' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="right">{formatNumber(page.uniqueTargetPages)}</TableCell>
                          <TableCell align="center">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                setBreakdownSourcePage(page.sourcePage);
                                setBreakdownTargetPage(null);
                                setBreakdownPage(0);
                                setBreakdownOpen(true);
                              }}
                            >
                              View Links
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={sourcePages?.totalCount || 0}
                  page={sourcePage}
                  onPageChange={(_e, page) => setSourcePage(page)}
                  rowsPerPage={sourcePageSize}
                  onRowsPerPageChange={(e) => {
                    setSourcePageSize(parseInt(e.target.value, 10));
                    setSourcePage(0);
                  }}
                />
              </>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Tab 2: Target Pages (Inbound) */}
      <TabPanel value={activeTab} index={2}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6">
                  <InboundIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Target Pages (Inbound Links)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pages receiving internal links. More inbound links = higher internal authority. Search engines view these as important.
                </Typography>
              </Box>
              <IconButton onClick={loadTargetPages} disabled={targetLoading}>
                <RefreshIcon />
              </IconButton>
            </Box>

            {targetLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ minWidth: 400 }}>Target Page URL</TableCell>
                        <TableCell align="right">Total Inbound Links</TableCell>
                        <TableCell align="right">Unique Sources</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {targetPages?.pages.map((page, index) => (
                        <TableRow key={page.targetPage} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {index < 3 && <Chip label={`#${index + 1}`} size="small" color="success" />}
                              <MuiLink href={page.targetPage} target="_blank" rel="noopener" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Tooltip title={page.targetPage}>
                                  <Typography variant="body2" noWrap sx={{ maxWidth: 350 }}>
                                    {page.targetPage}
                                  </Typography>
                                </Tooltip>
                                <OpenIcon fontSize="small" />
                              </MuiLink>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Chip 
                              label={formatNumber(page.totalInboundLinks)} 
                              color={page.totalInboundLinks >= 10 ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="right">{formatNumber(page.uniqueSourcePages)}</TableCell>
                          <TableCell align="center">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                setBreakdownTargetPage(page.targetPage);
                                setBreakdownSourcePage(null);
                                setBreakdownPage(0);
                                setBreakdownOpen(true);
                              }}
                            >
                              View Sources
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={targetPages?.totalCount || 0}
                  page={targetPage}
                  onPageChange={(_e, page) => setTargetPage(page)}
                  rowsPerPage={targetPageSize}
                  onRowsPerPageChange={(e) => {
                    setTargetPageSize(parseInt(e.target.value, 10));
                    setTargetPage(0);
                  }}
                />
              </>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Tab 3: Orphan Pages */}
      <TabPanel value={activeTab} index={3}>
        {/* Orphan Pages Alert Banner */}
        {orphanPages && orphanPages.orphanPagesCount > 0 && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              {orphanPages.orphanPagesCount} Orphan Pages Detected
            </Typography>
            <Typography variant="body2">
              These pages have no internal links pointing to them. Search engines may have difficulty discovering and crawling these pages,
              which can negatively impact their rankings. Consider adding internal links from relevant content pages.
            </Typography>
          </Alert>
        )}

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6">
                  <OrphanIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Orphan Pages (No Inbound Links)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pages that are not linked from any other page on your site. These are invisible to crawlers navigating your site.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Chip 
                  label={`${orphanPages?.orphanPagesCount || 0} orphan pages`} 
                  color={orphanPages && orphanPages.orphanPagesCount > 0 ? 'warning' : 'success'}
                />
                <IconButton onClick={loadOrphanPages} disabled={orphanLoading}>
                  <RefreshIcon />
                </IconButton>
              </Box>
            </Box>

            {orphanLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : orphanPages && orphanPages.orphanPagesCount === 0 ? (
              <Alert severity="success">
                <Typography variant="subtitle1" fontWeight="bold">All Clear!</Typography>
                <Typography variant="body2">
                  All pages on your site have at least one internal link pointing to them.
                </Typography>
              </Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ minWidth: 500 }}>Orphan Page URL</TableCell>
                      <TableCell align="center">Status</TableCell>
                      <TableCell align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orphanPages?.orphanPages.map((pageUrl) => (
                      <TableRow key={pageUrl} hover>
                        <TableCell>
                          <MuiLink href={pageUrl} target="_blank" rel="noopener" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                              {pageUrl}
                            </Typography>
                            <OpenIcon fontSize="small" />
                          </MuiLink>
                        </TableCell>
                        <TableCell align="center">
                          <Chip label="No Inbound Links" color="warning" size="small" icon={<WarningIcon />} />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Add internal links to this page from related content">
                            <Button size="small" variant="outlined" color="warning">
                              Needs Links
                            </Button>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* SEO Recommendation */}
        {orphanPages && orphanPages.orphanPagesCount > 0 && (
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                💡 SEO Recommendations
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    1. Add Contextual Links
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Link to orphan pages from related blog posts or content pages using relevant anchor text.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    2. Update Navigation
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Consider adding important orphan pages to your site's navigation, footer, or sidebar.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    3. Create Hub Pages
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Build category or topic pages that naturally link to multiple related orphan pages.
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}
      </TabPanel>

      {/* Tab 5: Broken Links */}
      <TabPanel value={activeTab} index={5}>
        {brokenLinks && brokenLinks.totalCount > 0 && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              {formatNumber(brokenLinks.totalCount)} Broken Internal Link{brokenLinks.totalCount !== 1 ? 's' : ''} Detected
            </Typography>
            <Typography variant="body2">
              These internal links point to pages returning 4xx/5xx status. Fix or remove them to protect crawl paths and user experience.
            </Typography>
          </Alert>
        )}

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6">
                  <WarningIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Broken Internal Links
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Internal links whose target page has the latest known HTTP 4xx/5xx status.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Chip
                  label={`${formatNumber(brokenLinks?.totalCount || 0)} broken links`}
                  color={brokenLinks && brokenLinks.totalCount > 0 ? 'error' : 'success'}
                  size="small"
                />
                <IconButton onClick={loadBrokenLinks} disabled={brokenLoading}>
                  <RefreshIcon />
                </IconButton>
              </Box>
            </Box>

            {brokenLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : brokenLinks && brokenLinks.totalCount === 0 ? (
              <Alert severity="success">
                <Typography variant="subtitle1" fontWeight="bold">All Clear!</Typography>
                <Typography variant="body2">
                  No internal links currently point to pages with 4xx/5xx status.
                </Typography>
              </Alert>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ minWidth: 320 }}>Source Page</TableCell>
                        <TableCell sx={{ minWidth: 320 }}>Broken Target</TableCell>
                        <TableCell align="center">HTTP Status</TableCell>
                        <TableCell align="right">Link Count</TableCell>
                        <TableCell align="center">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {brokenLinks?.links.map((link) => (
                        <TableRow key={`${link.sourcePage}-${link.targetPage}-${link.httpStatus}`} hover>
                          <TableCell>
                            <MuiLink href={link.sourcePage} target="_blank" rel="noopener" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Tooltip title={link.sourcePage}>
                                <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                                  {extractPath(link.sourcePage)}
                                </Typography>
                              </Tooltip>
                              <OpenIcon fontSize="small" />
                            </MuiLink>
                          </TableCell>
                          <TableCell>
                            <MuiLink href={link.targetPage} target="_blank" rel="noopener" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Tooltip title={link.targetPage}>
                                <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                                  {extractPath(link.targetPage)}
                                </Typography>
                              </Tooltip>
                              <OpenIcon fontSize="small" />
                            </MuiLink>
                          </TableCell>
                          <TableCell align="center">
                            <Chip label={link.httpStatus} color="error" size="small" />
                          </TableCell>
                          <TableCell align="right">{formatNumber(link.linkCount)}</TableCell>
                          <TableCell align="center">
                            <Tooltip title="Update this source page to point to a live destination or remove the link">
                              <Button size="small" variant="outlined" color="error">
                                Fix Link
                              </Button>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={brokenLinks?.totalCount || 0}
                  page={brokenPage}
                  onPageChange={(_e, page) => setBrokenPage(page)}
                  rowsPerPage={brokenPageSize}
                  onRowsPerPageChange={(e) => {
                    setBrokenPageSize(parseInt(e.target.value, 10));
                    setBrokenPage(0);
                  }}
                />
              </>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Tab 4: Link Changes */}
      <TabPanel value={activeTab} index={4}>
        {linkChanges && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrendingUpIcon color="success" fontSize="large" />
                    <Box>
                      <Typography variant="body2" color="text.secondary">Internal Links Gained</Typography>
                      <Typography variant="h4" color="success.main">{formatNumber(linkChanges.gainedCount)}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrendingDownIcon color="error" fontSize="large" />
                    <Box>
                      <Typography variant="body2" color="text.secondary">Internal Links Lost</Typography>
                      <Typography variant="h4" color="error.main">{formatNumber(linkChanges.lostCount)}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Time Series Chart */}
        {dailyChanges && chartData.length > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <TimelineIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Daily Internal Link Changes
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <RechartsTooltip />
                  <Legend />
                  <Line type="monotone" dataKey="gained" stroke="#4caf50" name="Gained" strokeWidth={2} />
                  <Line type="monotone" dataKey="lost" stroke="#f44336" name="Lost" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Internal Link Changes History</Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  select
                  size="small"
                  label="Change Type"
                  value={changeTypeFilter}
                  onChange={(e) => setChangeTypeFilter(e.target.value as any)}
                  sx={{ width: 150 }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="GAINED">Gained</MenuItem>
                  <MenuItem value="LOST">Lost</MenuItem>
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Date Range"
                  value={changeDateRange}
                  onChange={(e) => setChangeDateRange(parseInt(e.target.value))}
                  sx={{ width: 150 }}
                >
                  <MenuItem value={7}>Last 7 days</MenuItem>
                  <MenuItem value={30}>Last 30 days</MenuItem>
                  <MenuItem value={90}>Last 90 days</MenuItem>
                </TextField>
                <IconButton onClick={loadLinkChanges} disabled={changesLoading}>
                  <RefreshIcon />
                </IconButton>
              </Box>
            </Box>

            {changesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : linkChanges && linkChanges.changes.length === 0 ? (
              <Alert severity="info">
                No internal link changes detected in the selected time period.
              </Alert>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Change Type</TableCell>
                        <TableCell>Source Page</TableCell>
                        <TableCell>Target Page</TableCell>
                        <TableCell>Change Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {linkChanges?.changes
                        .filter(change => change.linkType === 'INTERNAL')
                        .map((change) => (
                          <TableRow key={change.id} hover>
                            <TableCell>
                              <Chip 
                                label={change.changeType} 
                                size="small" 
                                color={change.changeType === 'GAINED' ? 'success' : 'error'}
                                icon={change.changeType === 'GAINED' ? <TrendingUpIcon /> : <TrendingDownIcon />}
                              />
                            </TableCell>
                            <TableCell>
                              <Tooltip title={change.sourceUrl || '-'}>
                                <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
                                  {change.sourceUrl ? extractPath(change.sourceUrl) : '-'}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <Tooltip title={change.targetUrl}>
                                <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
                                  {extractPath(change.targetUrl)}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell>{formatDate(change.changeDate)}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={linkChanges?.totalCount || 0}
                  page={changesPage}
                  onPageChange={(_e, page) => setChangesPage(page)}
                  rowsPerPage={changesPageSize}
                  onRowsPerPageChange={(e) => {
                    setChangesPageSize(parseInt(e.target.value, 10));
                    setChangesPage(0);
                  }}
                />
              </>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Breakdown Dialog */}
      <Dialog
        open={breakdownOpen}
        onClose={() => setBreakdownOpen(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>
          {breakdownSourcePage
            ? `Outbound Links from: ${extractPath(breakdownSourcePage)}`
            : `Inbound Links to: ${extractPath(breakdownTargetPage || '')}`}
        </DialogTitle>
        <DialogContent dividers>
          {breakdownLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer sx={{ maxHeight: 520 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      {breakdownSourcePage ? (
                        <TableCell sx={{ minWidth: 400 }}>Target Page</TableCell>
                      ) : (
                        <TableCell sx={{ minWidth: 400 }}>Source Page</TableCell>
                      )}
                      <TableCell sx={{ minWidth: 180 }}>Link Text</TableCell>
                      <TableCell align="right">Link Count</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {breakdownLinks?.links.map((link) => (
                      <TableRow key={link.id} hover>
                        {breakdownSourcePage ? (
                          <TableCell>
                            <MuiLink href={link.targetPage} target="_blank" rel="noopener">
                              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                {link.targetPage}
                              </Typography>
                            </MuiLink>
                          </TableCell>
                        ) : (
                          <TableCell>
                            <MuiLink href={link.sourcePage} target="_blank" rel="noopener">
                              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                {link.sourcePage}
                              </Typography>
                            </MuiLink>
                          </TableCell>
                        )}
                        <TableCell>
                          <Tooltip title={link.linkText || 'No anchor text'}>
                            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                              {link.linkText || <em style={{ color: '#999' }}>No text</em>}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          <Chip label={link.linkCount} size="small" color={link.linkCount > 1 ? 'primary' : 'default'} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={breakdownLinks?.totalCount || 0}
                page={breakdownPage}
                onPageChange={(_e, page) => setBreakdownPage(page)}
                rowsPerPage={breakdownPageSize}
                onRowsPerPageChange={(e) => {
                  setBreakdownPageSize(parseInt(e.target.value, 10));
                  setBreakdownPage(0);
                }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => {
              setBreakdownOpen(false);
              setBreakdownSourcePage(null);
              setBreakdownTargetPage(null);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
